import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const baseInputClassName =
  "rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30";

const ssccInputClassName = `block w-full min-w-[8rem] max-w-[14rem] ${baseInputClassName}`;
const narrowInputClassName = `block w-20 ${baseInputClassName}`;

export default function SunOrderProductsTable(props: any) {
  const {
    loading,
    values,
    onProductFieldChange,
    onItemEditComplete,
    onUnitPriceEditComplete,
    askDeleteOrderItem,
    openSunShippingLabelReview,
    showLabelAction = false,
  } = props;

  const productBody = (rowData: any) => (
    <div className="py-0.5">
      <p className="font-semibold text-slate-900">{rowData.product_name}</p>
      <p className="text-xs text-slate-500">
        PLU: {rowData.plu ?? `SUN-${rowData.product_id}`}
      </p>
    </div>
  );

  const fieldInputBody = (
    rowData: any,
    { rowIndex }: { rowIndex: number },
    field: "sscc_hri_number" | "quantity" | "unit_price",
    inputType: "text" | "number" = "text"
  ) => {
    const value =
      rowData[field] === null || rowData[field] === undefined
        ? ""
        : rowData[field];

    const className =
      field === "sscc_hri_number" ? ssccInputClassName : narrowInputClassName;

    return (
      <input
        type={inputType}
        step={inputType === "number" ? "any" : undefined}
        min={inputType === "number" ? 0 : undefined}
        className={className}
        value={value}
        onChange={(e) =>
          onProductFieldChange?.(rowIndex, field, e.target.value)
        }
        onBlur={() => {
          const current = values.products[rowIndex];
          if (!current) return;

          if (field === "unit_price") {
            onUnitPriceEditComplete?.(current, rowIndex);
            return;
          }

          onItemEditComplete?.(current, rowIndex);
        }}
      />
    );
  };

  const totalPriceBody = (rowData: any) => {
    const total = rowData.total_dollar_amount ?? 0;
    return (
      <p className="text-right text-sm font-medium text-slate-900">
        ${parseFloat(total || 0).toFixed(2)}
      </p>
    );
  };

  const actionsBody = (rowData: any) => (
    <div className="flex items-center justify-end gap-2">
      {showLabelAction ? (
        <button
          type="button"
          onClick={() => openSunShippingLabelReview?.(rowData)}
          className="rounded-md bg-teal-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          Label
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => askDeleteOrderItem(rowData)}
        className="rounded-md bg-red-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
      >
        Delete
      </button>
    </div>
  );

  const totalCases = (values.products ?? []).reduce(
    (sum: number, item: any) => sum + parseFloat(item.quantity ?? 0),
    0
  );

  return (
    <div className="mt-2">
      {loading ? (
        <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
          <div className="animate-pulse flex space-x-4">Loading</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <DataTable
            value={values.products}
            dataKey="id"
            tableClassName="min-w-full bg-white text-sm sun-order-products-table-parent"
            cellClassName={() =>
              "px-3 py-2.5 border-b border-slate-100 text-sm align-middle"
            }
          >
            <Column
              header="Product"
              body={productBody}
              headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            />
            <Column
              header="SSCC HRI #"
              body={(rowData, options) =>
                fieldInputBody(rowData, options, "sscc_hri_number", "text")
              }
              headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
              style={{ width: "15.0rem" }}
            />
            <Column
              header="Qty"
              body={(rowData, options) =>
                fieldInputBody(rowData, options, "quantity", "number")
              }
              headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
              style={{ width: "6.5rem" }}
            />
            <Column
              header="Unit price"
              body={(rowData, options) =>
                fieldInputBody(rowData, options, "unit_price", "number")
              }
              headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
              style={{ width: "6.5rem" }}
            />
            <Column
              header="Total price"
              body={totalPriceBody}
              headerClassName="bg-slate-50 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
              bodyClassName="text-right"
              style={{ width: "7rem" }}
            />
            <Column
              header=""
              body={actionsBody}
              headerClassName="bg-slate-50 px-3 py-2.5"
              style={{ textAlign: "right" }}
            />
          </DataTable>

          <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3">
            <div className="ml-auto w-full max-w-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">
                  Subtotal
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  $
                  {parseFloat(`${values.subtotal_dollar_amount || 0}`).toFixed(
                    2
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Tax</span>
                <span className="text-sm font-semibold text-slate-900">
                  $0.00
                </span>
              </div>
              <div className="border-t border-slate-200 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-slate-900">
                    Total
                  </span>
                  <span className="text-base font-bold text-slate-900">
                    $
                    {parseFloat(`${values.total_dollar_amount || 0}`).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-slate-900">
                  Total Cases
                </span>
                <span className="text-base font-bold text-slate-900">
                  {totalCases}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
